import type { ImageFormat } from '../converter/image-encoder'
import type { QualityMode } from '../converter/quality'
import type { UploadedPdf } from './uploader'
import { formatBytes } from '../utils/format'
import { isMobile } from '../utils/browser'
import { createPageSelector } from './page-selector'

export type ConverterOptionsState = {
  format: ImageFormat
  quality: QualityMode
  pages: number[]
}

/**
 * Renders the converter options screen showing file info,
 * format/quality/page selection, and a convert button.
 * Calls onConvert with the selected options when the user
 * clicks "Convert PDF".
 */
export function createConverterOptions(
  container: HTMLElement,
  uploaded: UploadedPdf,
  onConvert: (state: ConverterOptionsState) => void,
): void {
  const { filename, fileSize, numPages, pdf } = uploaded

  container.innerHTML = `
    <div class="options-panel">
      <div class="file-info">
        <p class="file-info__name"></p>
        <p class="file-info__meta">${numPages} ${numPages === 1 ? 'page' : 'pages'} · ${formatBytes(fileSize)}</p>
      </div>

      <fieldset class="option-group">
        <legend>Format</legend>
        <label class="radio-option">
          <input type="radio" name="format" value="jpeg" checked />
          <span class="radio-option__label">JPG</span>
          <span class="radio-option__hint">Smaller file size</span>
        </label>
        <label class="radio-option">
          <input type="radio" name="format" value="png" />
          <span class="radio-option__label">PNG</span>
          <span class="radio-option__hint">Lossless, best for text</span>
        </label>
      </fieldset>

      <fieldset class="option-group">
        <legend>Quality</legend>
        <label class="radio-option">
          <input type="radio" name="quality" value="smart-hd" checked />
          <span class="radio-option__label">Smart HD</span>
          <span class="radio-option__hint">300 DPI · Recommended</span>
        </label>
        <label class="radio-option">
          <input type="radio" name="quality" value="maximum" />
          <span class="radio-option__label">Maximum</span>
          <span class="radio-option__hint">450 DPI · More memory, slower</span>
        </label>
      </fieldset>

      <p class="quality-warning" id="qualityWarning" hidden>
        Maximum quality on mobile devices uses a lot of memory and may crash.
        Consider Smart HD for better reliability.
      </p>

      <fieldset class="option-group">
        <legend>Pages</legend>
        <label class="radio-option">
          <input type="radio" name="pageMode" value="all" checked />
          <span class="radio-option__label">All pages</span>
        </label>
        <label class="radio-option">
          <input type="radio" name="pageMode" value="pick" />
          <span class="radio-option__label">Pick pages</span>
          <span class="radio-option__hint">Select &amp; reorder thumbnails</span>
        </label>
        <div class="page-selector-wrap" id="pageSelectorWrap" hidden></div>
      </fieldset>

      <button type="button" class="convert-button" id="convertButton">
        Convert PDF
      </button>
      <p class="error-message" id="errorMessage" role="alert" aria-live="polite"></p>
    </div>
  `

  const nameEl = container.querySelector<HTMLParagraphElement>('.file-info__name')!
  nameEl.textContent = filename

  const pageSelectorWrap =
    container.querySelector<HTMLDivElement>('#pageSelectorWrap')!
  const errorMessage =
    container.querySelector<HTMLParagraphElement>('#errorMessage')!
  const convertButton =
    container.querySelector<HTMLButtonElement>('#convertButton')!
  const qualityWarning =
    container.querySelector<HTMLParagraphElement>('#qualityWarning')!

  const mobile = isMobile()

  container
    .querySelectorAll<HTMLInputElement>('input[name="quality"]')
    .forEach((radio) => {
      radio.addEventListener('change', () => {
        if (mobile && radio.value === 'maximum') {
          qualityWarning.hidden = false
        } else {
          qualityWarning.hidden = true
        }
      })
    })

  // Page selector state — initialized with all pages selected
  let selectedPages: number[] = Array.from({ length: numPages }, (_, i) => i + 1)
  let pageSelectorInstance: { getSelectedPages: () => number[] } | null = null
  let pageSelectorInitialized = false

  container
    .querySelectorAll<HTMLInputElement>('input[name="pageMode"]')
    .forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.value === 'pick') {
          pageSelectorWrap.hidden = false
          if (!pageSelectorInitialized) {
            pageSelectorInstance = createPageSelector(
              pageSelectorWrap,
              pdf,
              numPages,
              (pages) => {
                selectedPages = pages
              },
            )
            // Start with all pages selected
            pageSelectorInitialized = true
          }
        } else {
          pageSelectorWrap.hidden = true
          // Reset to all pages when switching back to "all"
          selectedPages = Array.from({ length: numPages }, (_, i) => i + 1)
        }
      })
    })

  convertButton.addEventListener('click', () => {
    errorMessage.textContent = ''

    const format: ImageFormat = container
      .querySelector<HTMLInputElement>('input[name="format"]:checked')!
      .value as ImageFormat

    const quality: QualityMode = container
      .querySelector<HTMLInputElement>('input[name="quality"]:checked')!
      .value as QualityMode

    const pageMode = container
      .querySelector<HTMLInputElement>('input[name="pageMode"]:checked')!.value

    let pages: number[]

    if (pageMode === 'all') {
      pages = Array.from({ length: numPages }, (_, i) => i + 1)
    } else {
      // Use the current selection from the page selector
      pages = pageSelectorInstance
        ? pageSelectorInstance.getSelectedPages()
        : selectedPages
    }

    if (pages.length === 0) {
      errorMessage.textContent = 'Select at least one page to convert.'
      return
    }

    onConvert({ format, quality, pages })
  })
}
